import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Anchor, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PASSWORD_MIN_LENGTH, getAdminPasswordIssue } from '@harbormaster/shared';
import { inviteApi, ApiError } from '../lib/api';
import { supabase } from '../lib/supabase';
import { tAdminPasswordIssue } from '../lib/passwordValidation';

interface InviteDetails {
  email: string;
  role: string;
  org_name: string | null;
  marina_name: string | null;
  expires_at: string;
}

export function InviteAcceptPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('Invalid invitation link');
      setLoading(false);
      return;
    }
    inviteApi.getInvite(token)
      .then(data => setInvite(data as unknown as InviteDetails))
      .catch(err => setLoadError(err instanceof ApiError ? err.message : 'Invalid or expired invitation'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const pwdIssue = getAdminPasswordIssue(password);
    if (pwdIssue) {
      setSubmitError(tAdminPasswordIssue(t, pwdIssue));
      return;
    }
    if (password !== confirm) {
      setSubmitError(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (!token) return;

    setSubmitting(true);
    try {
      const result = await inviteApi.acceptInvite(token, { name, password }) as {
        auto_login: boolean;
        access_token?: string;
        refresh_token?: string;
      };

      if (result.auto_login && result.access_token && result.refresh_token) {
        await supabase.auth.setSession({
          access_token: result.access_token,
          refresh_token: result.refresh_token,
        });
        setDone(true);
        setTimeout(() => navigate('/'), 1500);
      } else {
        setDone(true);
        setTimeout(() => navigate('/login'), 1500);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_TAKEN') {
        setSubmitError(t('auth.emailAlreadyExistsInvite'));
      } else {
        setSubmitError(err instanceof ApiError ? err.message : 'Failed to create account');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
            <Anchor className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-gray-900">Virtual Harbormaster</span>
        </div>

        {loadError ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="font-semibold text-gray-900 mb-1">{t('auth.inviteInvalid')}</h2>
            <p className="text-sm text-gray-500">{loadError}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700"
            >
              {t('auth.signIn')}
            </button>
          </div>
        ) : done ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="font-semibold text-gray-900 mb-1">{t('auth.accountCreated')}</h2>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="mb-5 text-center">
              <h1 className="text-lg font-bold text-gray-900 mb-1">{t('auth.welcomeTitle')}</h1>
              {invite && (
                <p className="text-sm text-gray-500">
                  <strong>{invite.marina_name ?? invite.org_name}</strong>
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('auth.email')}</label>
                <input
                  type="email"
                  value={invite?.email ?? ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('auth.name')} *</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('auth.password')} *</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={ADMIN_PASSWORD_MIN_LENGTH}
                  autoComplete="new-password"
                  placeholder={t('auth.passwordPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                />
                <p className="text-xs text-gray-500 mt-1">{t('auth.passwordRequirementsHint')}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('auth.confirmPassword')} *</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  minLength={ADMIN_PASSWORD_MIN_LENGTH}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                />
              </div>

              {submitError && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 mt-1"
              >
                {submitting ? t('auth.creatingAccount') : t('auth.createAccount')}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
